/** @jsxImportSource @emotion/react */
import React, { useMemo } from 'react';

import { IStation, IStationConnector } from '../../../../api/station/types';

import { EStationPricingOption } from '../../../../api/types';
import { availableConnectorStatuses, connectedConnectorStatuses } from '../../../../constants';
import {
    getConnectorIdBarWidth,
    getEffectiveConnectorIdentifier,
    shouldShowConnectorNumber,
    shouldShowShortCode,
} from '../../../../utils/connector-identifier';

import setConnectorStyles from './set-connector.styles';
import { ILocation } from '../../../../api/locations/types';
import { ISetUpState } from '../../set-up.types';
import ConnectorInfoContainer from './ConnectorInfoContainer';

type Props = {
    onPress: (state: ISetUpState['connector'] & { station: IStation }) => void;
    connector: IStationConnector & {
        station: IStation;
        connectorNumber: string;
    };
    station: IStation;
    isUnavailable?: boolean;
    location?: ILocation;
};

const Connector = (props: Props) => {
    const { connector, onPress, station, isUnavailable = false } = props;

    // TODO add connector pricing option in the future
    const isFree = useMemo(
        () => connector.tariff?.pricingOption === EStationPricingOption.free,
        [station]
    );

    const isAvailable = useMemo(
        () => availableConnectorStatuses.includes(connector.status),
        [connector.status]
    );

    const isCarConnected = useMemo(
        () => connectedConnectorStatuses.includes(connector.status),
        [connector.status]
    );

    const isAvailableToClick = (isAvailable || isCarConnected) && !isUnavailable;

    const connectorIdentifier = getEffectiveConnectorIdentifier(
        props.location?.connectorIdentifier ?? station.stationLocation?.connectorIdentifier
    );

    const showConnectorNumber = shouldShowConnectorNumber(connectorIdentifier);
    const showShortCode = shouldShowShortCode(connectorIdentifier);
    const idBarWidth = getConnectorIdBarWidth(connectorIdentifier);

    return (
        <div
            css={[
                setConnectorStyles.connector,
                !isAvailable && !isUnavailable ? setConnectorStyles.connectorIdBusy : {},
                isUnavailable ? setConnectorStyles.unavailable : {},
                isAvailableToClick ? setConnectorStyles.cursorStyles : {},
            ]}
            key={connector._id}
            onClick={() => {
                if (isAvailableToClick) {
                    onPress(connector);
                }
            }}>
            <div
                css={[
                    setConnectorStyles.connectorId,
                    !showConnectorNumber && setConnectorStyles.connectorIdThin,
                    !isAvailable && !isUnavailable ? setConnectorStyles.connectorIdBusy : {},
                    isUnavailable ? setConnectorStyles.unavailable : {},
                ]}>
                {showConnectorNumber ? connector.connectorNumber : null}
            </div>
            <div css={[setConnectorStyles.rightSide, { width: `calc(100% - ${idBarWidth}px)` }]}>
                <ConnectorInfoContainer
                    isUnavailable={isUnavailable}
                    connector={connector}
                    isFree={isFree}
                    station={station}
                    connectorIdentifier={connectorIdentifier}
                    showShortCode={showShortCode}
                />
            </div>
        </div>
    );
};

export default Connector;
